/**
 * Centralized utility for appointment time slot logic
 */

export const LUNCH_BREAK = {
  start: 12 * 60,
  end: 13 * 60
};

export const SERVICE_RULES = {
  sti_testing: {
    name: "STI Testing / Screening",
    duration: 20, // minutes
    closingTime: "5:00 PM",
    separateBookingType: true // 1 Online + 1 Walk-in
  },
  family_planning: {
    name: "Family Planning / Contraceptive Services",
    duration: 20, // minutes
    closingTime: "2:40 PM",
    separateBookingType: false // 1 TOTAL
  },
  rh_consult: {
    name: "Reproductive Health Consultation",
    duration: 30, // minutes
    closingTime: "5:00 PM",
    separateBookingType: true // 1 Online + 1 Walk-in
  }
};

export const SERVICE_FILTER_OPTIONS = [
  { value: "rh_consult", label: "Reproductive Health Consultation" },
  { value: "family_planning", label: "Family Planning / Contraceptives Services" },
  { value: "sti_testing", label: "STI Testing / Screening" }
];

const normalizeServiceText = (value) => String(value || "")
  .replace(/<br\s*\/?>/gi, " ")
  .replace(/<[^>]*>/g, " ")
  .replace(/&nbsp;/gi, " ")
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();

export const getServiceFilterKey = (value) => {
  const normalized = normalizeServiceText(value);
  if (!normalized) return "";
  if (normalized === "rh_consult" || normalized.includes("reproductive health") || normalized.includes("rh consultation")) {
    return "rh_consult";
  }
  if (
    normalized === "family_planning" ||
    normalized.includes("family planning") ||
    normalized.includes("contraceptive") ||
    normalized.includes("contraceptives")
  ) {
    return "family_planning";
  }
  if (normalized === "sti_testing" || normalized.includes("sti testing") || normalized.includes("screening")) {
    return "sti_testing";
  }
  return normalized;
};

export const matchesServiceFilter = (item, selectedService) => {
  if (!selectedService) return true;
  const selectedKey = getServiceFilterKey(selectedService);
  return [item?.serviceId, item?.serviceName].some(
    (value) => getServiceFilterKey(value) === selectedKey
  );
};

/**
 * Maps service name or ID to the normalized rule key
 */
export const getServiceRule = (serviceIdOrName) => {
  if (!serviceIdOrName) return null;
  
  // Try direct ID match
  if (SERVICE_RULES[serviceIdOrName]) return SERVICE_RULES[serviceIdOrName];
  
  // Try case-insensitive name match
  const name = serviceIdOrName.toLowerCase();
  if (name.includes("sti testing") || name.includes("screening")) return SERVICE_RULES.sti_testing;
  if (name.includes("family planning") || name.includes("contraceptive")) return SERVICE_RULES.family_planning;
  if (name.includes("reproductive health") || name.includes("consultation")) return SERVICE_RULES.rh_consult;
  
  return null;
};

/**
 * Converts "8:00 AM" to { hours: 8, minutes: 0 }
 */
export const parseTimeString = (timeStr) => {
  const [time, period] = timeStr.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return { hours, minutes };
};

/**
 * Converts { hours: 8, minutes: 0 } to "8:00 AM"
 */
export const formatTimeToString = (hours, minutes) => {
  const period = hours >= 12 ? 'PM' : 'AM';
  let displayHours = hours % 12;
  if (displayHours === 0) displayHours = 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Validates whether a start time is blocked by the clinic lunch break or service hours.
 */
export const getSlotValidation = (timeStr, serviceIdOrName, durationOverride = null) => {
  const rule = getServiceRule(serviceIdOrName);
  if (!timeStr || !rule) {
   return { valid: true, reason: null };
  }

  const duration = durationOverride ?? rule.duration ?? 20;
  const startMinutes = (parseTimeString(timeStr).hours * 60) + parseTimeString(timeStr).minutes;
  const endMinutes = startMinutes + duration;
  const closingMinutes = (parseTimeString(rule.closingTime).hours * 60) + parseTimeString(rule.closingTime).minutes;

  if (startMinutes < 8 * 60 || endMinutes > closingMinutes) {
   return { valid: false, reason: 'service_hours' };
  }

  if (startMinutes < LUNCH_BREAK.end && endMinutes > LUNCH_BREAK.start) {
   return { valid: false, reason: 'lunch' };
  }

  if (startMinutes === LUNCH_BREAK.start || (startMinutes < LUNCH_BREAK.end && endMinutes > LUNCH_BREAK.start)) {
   return { valid: false, reason: 'lunch' };
  }

  return { valid: true, reason: null };
};

/**
 * Generates dynamic slots based on service rules
 */
export const generateDynamicSlots = (serviceIdOrName) => {
  const rule = getServiceRule(serviceIdOrName);
  if (!rule) return { morning: [], afternoon: [] };

  const startHour = 8;
  const startMinute = 0;
  const closingMinutes = (parseTimeString(rule.closingTime).hours * 60) + parseTimeString(rule.closingTime).minutes;
  const duration = rule.duration;

  const morning = [];
  const afternoon = [];

  let currentMinutes = (startHour * 60) + startMinute;

  while (currentMinutes < closingMinutes) {
    const slotEndMinutes = currentMinutes + duration;
    const timeStr = formatTimeToString(Math.floor(currentMinutes / 60), currentMinutes % 60);

    if (slotEndMinutes <= closingMinutes && !(currentMinutes < LUNCH_BREAK.end && slotEndMinutes > LUNCH_BREAK.start)) {
      if (currentMinutes < LUNCH_BREAK.start) {
        morning.push(timeStr);
      } else {
        afternoon.push(timeStr);
      }
    }

    currentMinutes += duration;
  }

  return { morning, afternoon };
};

/**
 * Helper to get YYYY-MM-DD in Asia/Manila timezone
 */
export const formatDateToPH = (date) => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Helper to get current PH Time components
 */
export const getPHNow = () => {
  const now = new Date();
  const phTimeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric',
    hour12: false
  });
  
  const parts = phTimeFormatter.formatToParts(now);
  return {
    year: parseInt(parts.find(p => p.type === 'year').value),
    month: parseInt(parts.find(p => p.type === 'month').value) - 1,
    day: parseInt(parts.find(p => p.type === 'day').value),
    hour: parseInt(parts.find(p => p.type === 'hour').value),
    minute: parseInt(parts.find(p => p.type === 'minute').value)
  };
};
